import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RiDeleteBinLine } from "react-icons/ri";

// Success Toast Component
const SuccessToast = ({ message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[9999] pointer-events-none">
      <div className="bg-green-600 text-green px-8 py-4 rounded-xl shadow-2xl pointer-events-auto animate-fade-in-scale">
        <div className="flex items-center gap-3">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="font-semibold text-lg">{message}</p>
        </div>
      </div>
    </div>
  );
};

// Error Toast Component
const ErrorToast = ({ message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[9999] pointer-events-none">
      <div className="bg-red-600 text-white px-8 py-4 rounded-xl shadow-2xl pointer-events-auto animate-fade-in-scale">
        <div className="flex items-center gap-3">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <p className="font-semibold text-lg">{message}</p>
        </div>
      </div>
    </div>
  );
};

function CompanyAccounts() {
  const nav = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showTruckIncome, setShowTruckIncome] = useState(false);
  const [showSalary, setShowSalary] = useState(false);
  const [showAddLoan, setShowAddLoan] = useState(false);
  const [showPayLoan, setShowPayLoan] = useState(false);
  const [showLoansList, setShowLoansList] = useState(false);
  
  // Toast notifications
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Form states
  const [newAccount, setNewAccount] = useState({
    name: '',
    type: 'bank',
    accountNumber: '',
    bankName: '',
    initialBalance: 0,
    currency: 'KES'
  });
  
  const [depositData, setDepositData] = useState({
    accountId: '',
    amount: 0,
    description: ''
  });
  
  const [truckIncomeData, setTruckIncomeData] = useState({
    toAccountId: '',
    vehicleId: '',
    amount: 0,
    description: ''
  });
  
  const [salaryData, setSalaryData] = useState({
    fromAccountId: '',
    amount: 0,
    employeeName: '',
    description: 'Monthly Salary'
  });
  
  const [loanData, setLoanData] = useState({
    vehicleId: '',
    loanAmount: 0,
    interestRate: 5,
    loanTermMonths: 60,
    paymentDay: 15,
    startDate: new Date().toISOString().split('T')[0],
    companyAccountId: ''
  });
  
  const [loanPaymentData, setLoanPaymentData] = useState({
    loanId: '',
    fromAccountId: '',
    amount: 0,
    description: ''
  });
  
  const [trucks, setTrucks] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [activeLoans, setActiveLoans] = useState([]);
  
  // const API_BASE = "http://localhost:8000";
  // const API_BASE = "http://192.168.100.9:8000";
  // const API_BASE = "https://abbebehauliers.fleetapi.exhibitsolutions.co.tz";
  const API_BASE = "https://www.fleetapi.exhibitsolutions.co.ke";

  // Toast helper functions
  const showSuccess = (message) => {
    setSuccessMessage(message);
  };

  const showError = (message) => {
    setErrorMessage(message);
  };

  useEffect(() => {
    fetchData();
    fetchTrucks();
  }, []);

  const fetchData = async () => {
    try {
      const [summaryRes, accountsRes, transactionsRes, loansRes] = await Promise.all([
        fetch(`${API_BASE}/company-accounts/financial-summary`),
        fetch(`${API_BASE}/company-accounts/accounts`),
        fetch(`${API_BASE}/company-accounts/transactions?limit=10`),
        fetch(`${API_BASE}/company-accounts/loans?status=active`)
      ]);

      const summaryData = await summaryRes.json();
      const accountsData = await accountsRes.json();
      const transactionsData = await transactionsRes.json();
      const loansData = await loansRes.json();

      if (summaryData.success) setFinancialSummary(summaryData.data);
      if (accountsData.success) setAccounts(accountsData.data);
      if (transactionsData.success) setTransactions(transactionsData.data);
      if (loansData.success) setActiveLoans(loansData.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      showError("Error loading data");
    }
  };

  const fetchTrucks = async () => {
    try {
      const response = await fetch(`${API_BASE}/vehicles/all-vehicles`);
      const data = await response.json();
      if (data.docs) {
        setTrucks(data.docs.map(vehicle => ({
          id: vehicle.id,
          plate_no: vehicle.plate_no,
          model: vehicle.make,
          fleet_no: vehicle.fleet_no
        })));
      }
    } catch (error) {
      console.error("Error fetching trucks:", error);
    }
  };

  // Create new account
  const handleCreateAccount = async () => {
    try {
      const response = await fetch(`${API_BASE}/company-accounts/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAccount)
      });
      
      const data = await response.json();
      if (data.success) {
        showSuccess('Account created successfully!');
        setShowAddAccount(false);
        setNewAccount({
          name: '',
          type: 'bank',
          accountNumber: '',
          bankName: '',
          initialBalance: 0,
          currency: 'KES'
        });
        fetchData();
      } else {
        showError(data.error || 'Error creating account');
      }
    } catch (error) {
      showError('Error creating account: ' + error.message);
    }
  };

  // Deposit money
  const handleDeposit = async () => {
    try {
      const response = await fetch(`${API_BASE}/company-accounts/deposit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(depositData)
      });
      
      const data = await response.json();
      if (data.success) {
        showSuccess('Deposit successful!');
        setShowDeposit(false);
        setDepositData({ accountId: '', amount: 0, description: '' });
        fetchData();
      } else {
        showError(data.error || 'Error processing deposit');
      }
    } catch (error) {
      showError('Error depositing money: ' + error.message);
    }
  };

  // Truck Income Deposit
  const handleTruckIncome = async () => {
    try {
      const response = await fetch(`${API_BASE}/company-accounts/truck-income`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(truckIncomeData)
      });
      
      const data = await response.json();
      if (data.success) {
        showSuccess('Truck income deposited successfully!');
        setShowTruckIncome(false);
        setTruckIncomeData({
          toAccountId: '',
          vehicleId: '',
          amount: 0,
          description: ''
        });
        fetchData();
      } else {
        showError(data.error || 'Error depositing truck income');
      }
    } catch (error) {
      showError('Error depositing truck income: ' + error.message);
    }
  };

  // Pay salary
  const handlePaySalary = async () => {
    try {
      const response = await fetch(`${API_BASE}/company-accounts/pay-salary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(salaryData)
      });
      
      const data = await response.json();
      if (data.success) {
        showSuccess('Salary paid successfully!');
        setShowSalary(false);
        setSalaryData({
          fromAccountId: '',
          amount: 0,
          employeeName: '',
          description: 'Monthly Salary'
        });
        fetchData();
      } else {
        showError(data.error || 'Error paying salary');
      }
    } catch (error) {
      showError('Error paying salary: ' + error.message);
    }
  };

  // Add loan to truck
  const handleAddLoan = async () => {
    if (!loanData.vehicleId) {
      showError('Please select a truck');
      return;
    }
    
    if (!loanData.companyAccountId) {
      showError('Please select a company account');
      return;
    }
    
    if (!loanData.loanAmount || loanData.loanAmount <= 0) {
      showError('Please enter a valid loan amount');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/company-accounts/loans/add`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...loanData,
          loanAmount: parseFloat(loanData.loanAmount),
          interestRate: parseFloat(loanData.interestRate),
          loanTermMonths: parseInt(loanData.loanTermMonths),
          paymentDay: parseInt(loanData.paymentDay)
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        showSuccess('Loan added successfully!');
        setShowAddLoan(false);
        setLoanData({
          vehicleId: '',
          loanAmount: 0,
          interestRate: 5,
          loanTermMonths: 60,
          paymentDay: 15,
          startDate: new Date().toISOString().split('T')[0],
          companyAccountId: ''
        });
        fetchData();
      } else {
        showError(data.error || 'Error adding loan');
      }
    } catch (error) {
      showError('Error adding loan: ' + error.message);
    }
  };

  // Pay loan installment
  const handlePayLoan = async () => {
    if (!loanPaymentData.loanId) {
      showError('Please select a loan');
      return;
    }
    
    if (!loanPaymentData.fromAccountId) {
      showError('Please select payment account');
      return;
    }
    
    if (!loanPaymentData.amount || loanPaymentData.amount <= 0) {
      showError('Please enter a valid amount');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/company-accounts/pay-loan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...loanPaymentData,
          amount: parseFloat(loanPaymentData.amount)
        })
      });
      
      const data = await response.json();
      if (data.success) {
        showSuccess('Loan payment successful!');
        setShowPayLoan(false);
        setLoanPaymentData({
          loanId: '',
          fromAccountId: '',
          amount: 0,
          description: ''
        });
        fetchData();
      } else {
        showError(data.error || 'Error paying loan');
      }
    } catch (error) {
      showError('Error paying loan: ' + error.message);
    }
  };

  // Delete loan
  const handleDeleteLoan = async (loanId, truckPlateNo) => {
    if (!window.confirm(`Are you sure you want to delete the loan for ${truckPlateNo}? This action cannot be undone and will also delete all associated payment records.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/company-accounts/loans/${loanId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      if (data.success) {
        showSuccess('Loan deleted successfully!');
        fetchData();
      } else {
        showError(data.error || 'Error deleting loan');
      }
    } catch (error) {
      showError('Error deleting loan: ' + error.message);
    }
  };

  // Delete account
  const handleDeleteAccount = async (accountId, accountName) => {
    if (!window.confirm(`Are you sure you want to delete "${accountName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/company-accounts/accounts/${accountId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      if (data.success) {
        showSuccess('Account deleted successfully!');
        fetchData();
      } else {
        showError(data.error || 'Error deleting account');
      }
    } catch (error) {
      showError('Error deleting account: ' + error.message);
    }
  };

  // Delete transaction
  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/company-accounts/transactions/${transactionId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      if (data.success) {
        showSuccess('Transaction deleted successfully!');
        fetchData();
      } else {
        showError(data.error || 'Error deleting transaction');
      }
    } catch (error) {
      showError('Error deleting transaction: ' + error.message);
    }
  };

  const formatCurrency = (amount) => {
    return `KES ${parseFloat(amount || 0).toLocaleString()}`;
  };

  const getAccountTypeIcon = (type) => {
    switch(type) {
      case 'bank': return '🏦';
      case 'cash': return '💵';
      case 'mobile_money': return '📱';
      default: return '🏛️';
    }
  };

  // Calculate monthly payment for preview
  const calculateMonthlyPayment = () => {
    const principal = parseFloat(loanData.loanAmount) || 0;
    const monthlyRate = parseFloat(loanData.interestRate) / 100 / 12;
    const months = parseInt(loanData.loanTermMonths) || 1;
    
    if (monthlyRate === 0) return principal / months;
    
    const payment = principal * monthlyRate * Math.pow(1 + monthlyRate, months) / 
                   (Math.pow(1 + monthlyRate, months) - 1);
    return isNaN(payment) ? 0 : payment;
  };

  return (
    <div className="p-4 sm:p-8 bg-light_grey min-h-screen">
      {/* Toast Notifications */}
      {successMessage && (
        <SuccessToast 
          message={successMessage} 
          onClose={() => setSuccessMessage('')} 
        />
      )}
      {errorMessage && (
        <ErrorToast 
          message={errorMessage} 
          onClose={() => setErrorMessage('')} 
        />
      )}

      {/* Navigation */}
      <div className="flex flex-wrap gap-2 sm:gap-4 mb-8">
        <button className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-orange-500 transition duration-200 font-semibold text-sm sm:text-base" 
          onClick={() => nav("/dashboard/finance")}>Finance</button>
        <button className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-orange-500 transition duration-200 font-semibold text-sm sm:text-base" 
          onClick={() => nav("/dashboard/finance/company-accounts")}>Company Accounts</button>
        <button className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-orange-500 transition duration-200 font-semibold text-sm sm:text-base" 
          onClick={() => nav("/dashboard/finance/statements")}>Statements</button>
        <button className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-orange-500 transition duration-200 font-semibold text-sm sm:text-base" 
          onClick={() => nav("/dashboard/finance/trucks")}>Truck/Trailer</button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 mb-8">
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs sm:text-sm">Total Balance</p>
              <p className="text-xl sm:text-3xl font-bold text-green-600">
                {financialSummary ? formatCurrency(financialSummary.totalBalance) : 'KES 0'}
              </p>
            </div>
            <span className="text-2xl sm:text-3xl">💰</span>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs sm:text-sm">Active Accounts</p>
              <p className="text-xl sm:text-3xl font-bold text-blue-600">
                {financialSummary ? financialSummary.totalAccounts : 0}
              </p>
            </div>
            <span className="text-2xl sm:text-3xl">🏦</span>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs sm:text-sm">Active Loans</p>
              <p className="text-xl sm:text-3xl font-bold text-orange-600">
                {activeLoans.length}
              </p>
            </div>
            <span className="text-2xl sm:text-3xl">🏦</span>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs sm:text-sm">Truck Income</p>
              <p className="text-xl sm:text-3xl font-bold text-green-600">
                {financialSummary ? formatCurrency(financialSummary.totalTruckIncome || 0) : 'KES 0'}
              </p>
            </div>
            <span className="text-2xl sm:text-3xl">🚚</span>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-xs sm:text-sm">Total Expenses</p>
              <p className="text-xl sm:text-3xl font-bold text-red-600">
                {financialSummary ? formatCurrency(financialSummary.totalExpenses) : 'KES 0'}
              </p>
            </div>
            <span className="text-2xl sm:text-3xl">↕️</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 sm:gap-4 mb-8">
        <button 
          onClick={() => setShowAddAccount(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-blue-700 transition duration-200 text-sm sm:text-base"
        >
          <span>+</span> Add Account
        </button>
        
        <button 
          onClick={() => setShowDeposit(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-blue-700 transition duration-200 text-sm sm:text-base"
        >
          <span>$</span>Deposit Money to Account
        </button>
        
        <button 
          onClick={() => setShowTruckIncome(true)}
          className="flex items-center gap-2 bg-orange-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-green-700 transition duration-200 text-sm sm:text-base"
        >
          <span>🚚💰</span> Truck Income
        </button>
        
        <button 
          onClick={() => setShowSalary(true)}
          className="flex items-center gap-2 bg-purple-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-purple-700 transition duration-200 text-sm sm:text-base"
        >
          <span>👥</span> Pay Salary
        </button>

        <button 
          onClick={() => setShowAddLoan(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-blue-700 transition duration-200 text-sm sm:text-base"
        >
          <span>🏦</span> Add Loan
        </button>
        
        <button 
          onClick={() => setShowPayLoan(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-blue-700 transition duration-200 text-sm sm:text-base"
        >
          <span>💰</span> Pay Loan
        </button>
        
        <button 
          onClick={() => setShowLoansList(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-blue-700 transition duration-200 text-sm sm:text-base"
        >
          <span>📋</span> View Loans
        </button>
      </div>

      {/* Accounts Grid */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl sm:text-2xl font-bold">Company Accounts</h2>
          <span className="text-gray-600 text-sm sm:text-base">{accounts.length} accounts</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {accounts.map(account => (
            <div 
              key={account.id}
              className="bg-white p-4 sm:p-6 rounded-xl shadow-md hover:shadow-lg transition duration-200 relative"
            >
              
              <div className="cursor-pointer" onClick={() => setSelectedAccount(account)}>
                <div className="flex justify-between items-start mb-4">
                  <div className="pr-8">
                    <h3 className="text-lg sm:text-xl font-bold">{account.name}</h3>
                    <p className="text-gray-500 text-sm">{account.type.toUpperCase()} • {account.currency}</p>
                    {account.bankName && <p className="text-xs sm:text-sm text-gray-500">{account.bankName}</p>}
                  </div>
                  <span className="text-2xl sm:text-3xl">{getAccountTypeIcon(account.type)}</span>
                </div>
                
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">Current Balance</span>
                    <span className="text-lg sm:text-2xl font-bold text-green-600">
                      {formatCurrency(account.currentBalance)}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteAccount(account.id, account.name);
                }}
                className="absolute top-4 right-4 text-gray-600 hover:text-red-1000 transition duration-300"
                title="Delete Account"
              >
                <RiDeleteBinLine color='red'/>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl sm:text-2xl font-bold">Recent Transactions</h2>
          <span className="text-gray-600 text-sm sm:text-base">{transactions.length} transactions</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm sm:text-base">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 sm:py-3 text-xs sm:text-base">Date</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-base">Type</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-base">From/To</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-base">Description</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-base">Amount</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-base">Actions</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(transaction => (
                <tr key={transaction.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 sm:py-3 text-xs sm:text-sm">
                    {new Date(transaction.date).toLocaleDateString()}
                  </td>
                  <td className="py-2 sm:py-3">
                    <span className={`px-2 sm:px-3 py-1 rounded-full text-xs ${
                      transaction.type === 'deposit' ? 'bg-green-100 text-green-800' :
                      transaction.type === 'truck_income' ? 'bg-green-100 text-green-800' :
                      transaction.type === 'loan_payment' ? 'bg-blue-100 text-blue-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {transaction.type.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="py-2 sm:py-3">
                    <div className="text-xs sm:text-sm">
                      {transaction.fromAccount?.name && (
                        <div>From: {transaction.fromAccount.name}</div>
                      )}
                      {transaction.toAccount?.name && (
                        <div>To: {transaction.toAccount.name}</div>
                      )}
                      {transaction.vehicle?.plate_no && (
                        <div>Truck: {transaction.vehicle.plate_no}</div>
                      )}
                    </div>
                  </td>
                  <td className="py-2 sm:py-3 text-xs sm:text-sm">{transaction.description}</td>
                  <td className={`py-2 sm:py-3 font-bold text-xs sm:text-sm ${
                    transaction.type === 'deposit' || transaction.type === 'truck_income' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {transaction.type === 'deposit' || transaction.type === 'truck_income' ? '+' : '-'} {formatCurrency(transaction.amount)}
                  </td>
                  <td className="py-2 sm:py-3">
                    <button 
                      onClick={() => handleDeleteTransaction(transaction.id)}
                      className="text-gray-400 hover:text-red-600 transition duration-200 p-1"
                      title="Delete Transaction"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODALS */}

      {/* Add Account Modal */}
      {showAddAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 sm:p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl sm:text-2xl font-bold">Add New Account</h3>
              <button 
                onClick={() => setShowAddAccount(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Account Name</label>
                <input 
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={newAccount.name}
                  onChange={(e) => setNewAccount({...newAccount, name: e.target.value})}
                  placeholder="e.g., Equity Bank Main"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Account Type</label>
                <select 
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={newAccount.type}
                  onChange={(e) => setNewAccount({...newAccount, type: e.target.value})}
                >
                  <option value="bank">Bank Account</option>
                  <option value="cash">Cash</option>
                  <option value="mobile_money">Mobile Money</option>
                </select>
              </div>
              
              {newAccount.type === 'bank' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Bank Name</label>
                    <input 
                      type="text"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={newAccount.bankName}
                      onChange={(e) => setNewAccount({...newAccount, bankName: e.target.value})}
                      placeholder="e.g., Equity Bank"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Account Number</label>
                    <input 
                      type="text"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={newAccount.accountNumber}
                      onChange={(e) => setNewAccount({...newAccount, accountNumber: e.target.value})}
                      placeholder="e.g., 123456789"
                    />
                  </div>
                </>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-1">Initial Balance (KES)</label>
                <input 
                  type="number"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={newAccount.initialBalance}
                  onChange={(e) => setNewAccount({...newAccount, initialBalance: e.target.value})}
                  placeholder="0"
                />
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <button 
                onClick={handleCreateAccount}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition duration-200 font-semibold"
              >
                Create Account
              </button>
              <button 
                onClick={() => setShowAddAccount(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deposit Modal */}
      {showDeposit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 sm:p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl sm:text-2xl font-bold">Deposit Money to Account</h3>
              <button 
                onClick={() => setShowDeposit(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Select Account</label>
                <select 
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={depositData.accountId}
                  onChange={(e) => setDepositData({...depositData, accountId: e.target.value})}
                >
                  <option value="">Select Account</option>
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name} - {formatCurrency(acc.currentBalance)}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Amount (KES)</label>
                <input 
                  type="number"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={depositData.amount}
                  onChange={(e) => setDepositData({...depositData, amount: e.target.value})}
                  placeholder="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <input 
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={depositData.description}
                  onChange={(e) => setDepositData({...depositData, description: e.target.value})}
                  placeholder="e.g., Capital injection"
                />
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <button 
                onClick={handleDeposit}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition duration-200 font-semibold"
              >
                Deposit Money
              </button>
              <button 
                onClick={() => setShowDeposit(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Truck Income Modal */}
      {showTruckIncome && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 sm:p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl sm:text-2xl font-bold">Truck Income Deposit</h3>
              <button 
                onClick={() => setShowTruckIncome(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Select Truck</label>
                <select 
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={truckIncomeData.vehicleId}
                  onChange={(e) => setTruckIncomeData({...truckIncomeData, vehicleId: e.target.value})}
                >
                  <option value="">Select Truck</option>
                  {trucks.map(truck => (
                    <option key={truck.id} value={truck.id}>
                      {truck.plate_no} - {truck.model} ({truck.fleet_no})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">To Account (Company Account)</label>
                <select 
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={truckIncomeData.toAccountId}
                  onChange={(e) => setTruckIncomeData({...truckIncomeData, toAccountId: e.target.value})}
                >
                  <option value="">Select Account</option>
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name} - Current: {formatCurrency(acc.currentBalance)}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Amount (KES)</label>
                <input 
                  type="number"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={truckIncomeData.amount}
                  onChange={(e) => setTruckIncomeData({...truckIncomeData, amount: e.target.value})}
                  placeholder="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <input 
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={truckIncomeData.description}
                  onChange={(e) => setTruckIncomeData({...truckIncomeData, description: e.target.value})}
                  placeholder="e.g., Trip to Mombasa income"
                />
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <button 
                onClick={handleTruckIncome}
                className="flex-1 bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 transition duration-200 font-semibold"
              >
                Deposit Income
              </button>
              <button 
                onClick={() => setShowTruckIncome(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pay Salary Modal */}
      {showSalary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 sm:p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl sm:text-2xl font-bold">Pay Salary</h3>
              <button 
                onClick={() => setShowSalary(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">From Account</label>
                <select 
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={salaryData.fromAccountId}
                  onChange={(e) => setSalaryData({...salaryData, fromAccountId: e.target.value})}
                >
                  <option value="">Select Account</option>
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name} - Available: {formatCurrency(acc.currentBalance)}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Employee Name</label>
                <input 
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={salaryData.employeeName}
                  onChange={(e) => setSalaryData({...salaryData, employeeName: e.target.value})}
                  placeholder="e.g., John Doe"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Amount (KES)</label>
                <input 
                  type="number"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={salaryData.amount}
                  onChange={(e) => setSalaryData({...salaryData, amount: e.target.value})}
                  placeholder="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <input 
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={salaryData.description}
                  onChange={(e) => setSalaryData({...salaryData, description: e.target.value})}
                  placeholder="e.g., January 2024 Salary"
                />
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <button 
                onClick={handlePaySalary}
                className="flex-1 bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition duration-200 font-semibold"
              >
                Pay Salary
              </button>
              <button 
                onClick={() => setShowSalary(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Loan Modal */}
      {showAddLoan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 sm:p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl sm:text-2xl font-bold">Add Loan to Truck</h3>
              <button 
                onClick={() => setShowAddLoan(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Select Truck *</label>
                <select 
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={loanData.vehicleId}
                  onChange={(e) => setLoanData({...loanData, vehicleId: e.target.value})}
                  required
                >
                  <option value="">Select Truck</option>
                  {trucks.map(truck => (
                    <option key={truck.id} value={truck.id}>
                      {truck.plate_no} - {truck.model} ({truck.fleet_no})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Company Account *</label>
                <select 
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={loanData.companyAccountId}
                  onChange={(e) => setLoanData({...loanData, companyAccountId: e.target.value})}
                  required
                >
                  <option value="">Select Account</option>
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name} ({acc.type})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Loan Amount (KES) *</label>
                <input 
                  type="number"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={loanData.loanAmount}
                  onChange={(e) => setLoanData({...loanData, loanAmount: e.target.value})}
                  placeholder="0"
                  required
                  min="1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Interest Rate (%) *</label>
                <input 
                  type="number"
                  step="0.1"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={loanData.interestRate}
                  onChange={(e) => setLoanData({...loanData, interestRate: e.target.value})}
                  placeholder="5"
                  required
                  min="0"
                  max="100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Loan Term (Months) *</label>
                <select 
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={loanData.loanTermMonths}
                  onChange={(e) => setLoanData({...loanData, loanTermMonths: e.target.value})}
                  required
                >
                  <option value="12">12 Months (1 Year)</option>
                  <option value="24">24 Months (2 Years)</option>
                  <option value="36">36 Months (3 Years)</option>
                  <option value="48">48 Months (4 Years)</option>
                  <option value="60">60 Months (5 Years)</option>
                  <option value="72">72 Months (6 Years)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Payment Day of Month *</label>
                <input 
                  type="number"
                  min="1"
                  max="31"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={loanData.paymentDay}
                  onChange={(e) => setLoanData({...loanData, paymentDay: e.target.value})}
                  placeholder="15"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Start Date *</label>
                <input 
                  type="date"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={loanData.startDate}
                  onChange={(e) => setLoanData({...loanData, startDate: e.target.value})}
                  required
                />
              </div>
              
              {/* Preview Section */}
              <div className="bg-gray-50 p-4 rounded-lg mt-4">
                <h4 className="font-bold mb-2">Loan Preview:</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-sm text-gray-600">Monthly Payment:</p>
                    <p className="font-bold text-lg">{formatCurrency(calculateMonthlyPayment())}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Interest:</p>
                    <p className="font-bold text-lg">
                      {formatCurrency((calculateMonthlyPayment() * loanData.loanTermMonths) - loanData.loanAmount)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <button 
                onClick={handleAddLoan}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition duration-200 font-semibold"
              >
                Create Loan
              </button>
              <button 
                onClick={() => setShowAddLoan(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pay Loan Modal */}
      {showPayLoan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 sm:p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl sm:text-2xl font-bold">Pay Loan Installment</h3>
              <button 
                onClick={() => setShowPayLoan(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Select Loan *</label>
                <select 
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={loanPaymentData.loanId}
                  onChange={(e) => setLoanPaymentData({...loanPaymentData, loanId: e.target.value})}
                  required
                >
                  <option value="">Select Loan</option>
                  {activeLoans.map(loan => (
                    <option key={loan.id} value={loan.id}>
                      {loan.vehicle?.plate_no} - Remaining: {formatCurrency(loan.remainingBalance)}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">From Account *</label>
                <select 
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={loanPaymentData.fromAccountId}
                  onChange={(e) => setLoanPaymentData({...loanPaymentData, fromAccountId: e.target.value})}
                  required
                >
                  <option value="">Select Account</option>
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name} - Available: {formatCurrency(acc.currentBalance)}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Amount (KES) *</label>
                <input 
                  type="number"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={loanPaymentData.amount}
                  onChange={(e) => setLoanPaymentData({...loanPaymentData, amount: e.target.value})}
                  placeholder="0"
                  required
                  min="1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <input 
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={loanPaymentData.description}
                  onChange={(e) => setLoanPaymentData({...loanPaymentData, description: e.target.value})}
                  placeholder="e.g., January 2024 loan payment"
                />
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <button 
                onClick={handlePayLoan}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition duration-200 font-semibold"
              >
                Pay Loan
              </button>
              <button 
                onClick={() => setShowPayLoan(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* IMPROVED COMPACT LOANS LIST MODAL - CENTERED & SHORTENED */}
      {showLoansList && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-4 w-full max-w-4xl max-h-[70vh] overflow-y-auto ml-auto mr-8">
            {/* Compact Header */}
            <div className="flex justify-between items-center mb-3 pb-2 border-b sticky top-0 bg-white z-10">
              <h3 className="text-lg font-bold">Active Loans ({activeLoans.length})</h3>
              <button 
                onClick={() => setShowLoansList(false)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>
            
            {activeLoans.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-gray-500 mb-3 text-sm">No active loans found.</p>
                <button 
                  onClick={() => {
                    setShowLoansList(false);
                    setShowAddLoan(true);
                  }}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition duration-200 text-sm"
                >
                  Add Your First Loan
                </button>
              </div>
            ) : (
              <>
                {/* Desktop Compact Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-semibold">Truck</th>
                        <th className="text-left py-2 px-3 font-semibold">Loan</th>
                        <th className="text-left py-2 px-3 font-semibold">Remaining</th>
                        <th className="text-left py-2 px-3 font-semibold">Paid</th>
                        <th className="text-left py-2 px-3 font-semibold">Monthly</th>
                        <th className="text-left py-2 px-3 font-semibold">Next Due</th>
                        <th className="text-left py-2 px-3 font-semibold">Status</th>
                        <th className="text-left py-2 px-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeLoans.map(loan => (
                        <tr key={loan.id} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-3">
                            <div className="font-medium text-xs">{loan.vehicle?.plate_no}</div>
                            <div className="text-[10px] text-gray-500 truncate max-w-[100px]">{loan.vehicle?.make}</div>
                          </td>
                          <td className="py-2 px-3 font-semibold text-xs whitespace-nowrap">{formatCurrency(loan.loanAmount)}</td>
                          <td className="py-2 px-3">
                            <div className="font-bold text-red-600 text-xs whitespace-nowrap">{formatCurrency(loan.remainingBalance)}</div>
                          </td>
                          <td className="py-2 px-3">
                            <div className="text-green-600 text-xs whitespace-nowrap">{formatCurrency(loan.paidAmount)}</div>
                          </td>
                          <td className="py-2 px-3 text-xs whitespace-nowrap">{formatCurrency(loan.monthlyPayment)}</td>
                          <td className="py-2 px-3 text-xs whitespace-nowrap">
                            {loan.nextPaymentDate ? new Date(loan.nextPaymentDate).toLocaleDateString('en-US', {month: 'short', day: 'numeric'}) : 'N/A'}
                          </td>
                          <td className="py-2 px-3">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                              loan.status === 'active' ? 'bg-green-100 text-green-800' :
                              loan.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {loan.status.toUpperCase()}
                            </span>
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex gap-1">
                              <button 
                                onClick={() => {
                                  setLoanPaymentData({
                                    loanId: loan.id,
                                    fromAccountId: '',
                                    amount: loan.monthlyPayment,
                                    description: `Payment for ${loan.vehicle?.plate_no}`
                                  });
                                  setShowPayLoan(true);
                                  setShowLoansList(false);
                                }}
                                className="bg-green-600 text-blue px-2 py-1 rounded hover:bg-green-700 transition duration-200 text-[10px] whitespace-nowrap"
                              >
                                Pay
                              </button>
                              <button 
                                onClick={() => handleDeleteLoan(loan.id, loan.vehicle?.plate_no)}
                                className="bg-red-600 text-blue px-2 py-1 rounded hover:bg-red-700 transition duration-200 text-[10px] whitespace-nowrap"
                                title="Delete"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Compact Card View */}
                <div className="md:hidden space-y-2">
                  {activeLoans.map(loan => (
                    <div key={loan.id} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                      {/* Compact Header */}
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-bold text-sm">{loan.vehicle?.plate_no}</h4>
                          <p className="text-[10px] text-gray-500">{loan.vehicle?.make}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          loan.status === 'active' ? 'bg-green-100 text-green-800' :
                          loan.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {loan.status.toUpperCase()}
                        </span>
                      </div>

                      {/* Compact Details Grid */}
                      <div className="grid grid-cols-3 gap-2 mb-2 text-[10px]">
                        <div>
                          <p className="text-gray-500">Loan</p>
                          <p className="font-bold">{formatCurrency(loan.loanAmount)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Remaining</p>
                          <p className="font-bold text-red-600">{formatCurrency(loan.remainingBalance)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Monthly</p>
                          <p className="font-bold">{formatCurrency(loan.monthlyPayment)}</p>
                        </div>
                      </div>

                      {/* Compact Actions */}
                      <div className="flex gap-2 pt-2 border-t">
                        <button 
                          onClick={() => {
                            setLoanPaymentData({
                              loanId: loan.id,
                              fromAccountId: '',
                              amount: loan.monthlyPayment,
                              description: `Payment for ${loan.vehicle?.plate_no}`
                            });
                            setShowPayLoan(true);
                            setShowLoansList(false);
                          }}
                          className="flex-1 bg-green-600 text-white py-1.5 rounded hover:bg-green-700 transition duration-200 text-xs"
                        >
                          Pay
                        </button>
                        <button 
                          onClick={() => handleDeleteLoan(loan.id, loan.vehicle?.plate_no)}
                          className="flex-1 bg-red-600 text-white py-1.5 rounded hover:bg-red-700 transition duration-200 text-xs"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            
            {/* Compact Footer */}
            <div className="mt-3 pt-3 border-t sticky bottom-0 bg-white">
              <button 
                onClick={() => setShowLoansList(false)}
                className="w-full bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition duration-200 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selected Account Details Modal */}  
      {selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 sm:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold">{selectedAccount.name}</h3>
                <p className="text-gray-500 text-sm">{selectedAccount.type.toUpperCase()} • {selectedAccount.currency}</p>
              </div>
              <button 
                onClick={() => setSelectedAccount(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-600 text-sm">Current Balance</p>
                <p className="text-2xl sm:text-3xl font-bold text-green-600">
                  {formatCurrency(selectedAccount.currentBalance)}
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-600 text-sm">Initial Balance</p>
                <p className="text-2xl sm:text-3xl font-bold text-blue-600">
                  {formatCurrency(selectedAccount.initialBalance)}
                </p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <button 
                onClick={() => {
                  setDepositData({
                    accountId: selectedAccount.id,
                    amount: 0,
                    description: ''
                  });
                  setShowDeposit(true);
                  setSelectedAccount(null);
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200 font-semibold"
              >
                Deposit Money to Account
              </button>
              <button 
                onClick={() => {
                  setLoanData(prev => ({
                    ...prev,
                    companyAccountId: selectedAccount.id
                  }));
                  setShowAddLoan(true);
                  setSelectedAccount(null);
                }}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition duration-200 font-semibold"
              >
                Add Loan (Linked)
              </button>
            </div>
            
            <div className="mt-6">
              <h4 className="text-lg font-bold mb-4">Account Details</h4>
              <div className="space-y-2 text-sm sm:text-base">
                <p><span className="font-medium">Account ID:</span> {selectedAccount.id}</p>
                <p><span className="font-medium">Created:</span> {new Date(selectedAccount.createdAt).toLocaleDateString()}</p>
                {selectedAccount.bankName && <p><span className="font-medium">Bank:</span> {selectedAccount.bankName}</p>}
                {selectedAccount.accountNumber && <p><span className="font-medium">Account #:</span> {selectedAccount.accountNumber}</p>}
              </div>
            </div>    
            
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <button 
                onClick={() => setSelectedAccount(null)}
                className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition duration-200 font-semibold"
              >
                Close
              </button>
              <button 
                onClick={() => {
                  if (window.confirm(`Are you sure you want to delete "${selectedAccount.name}"? This action cannot be undone.`)) {
                    handleDeleteAccount(selectedAccount.id, selectedAccount.name);
                    setSelectedAccount(null);
                  }
                }}
                className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition duration-200 font-semibold"
              >
                🗑️ Delete Account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add CSS for animations */}
      <style jsx>{`
        @keyframes fade-in-scale {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-fade-in-scale {
          animation: fade-in-scale 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default CompanyAccounts;